import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import {StyleSheet, View} from 'react-native';

type Entry = {id: string; node: ReactNode};

type ModalHostApi = {
  upsert: (id: string, node: ReactNode) => void;
  remove: (id: string) => void;
};

const ModalHostContext = createContext<ModalHostApi | null>(null);

export function useModalHost(): ModalHostApi {
  const ctx = useContext(ModalHostContext);
  if (!ctx) {
    throw new Error('useModalHost must be used within a <ModalHostProvider>');
  }
  return ctx;
}

const BASE_Z = 1000;

export function ModalHostProvider({children}: {children: ReactNode}) {
  const [entries, setEntries] = useState<Entry[]>([]);

  const api = useMemo<ModalHostApi>(
    () => ({
      upsert: (id, node) =>
        setEntries(prev => {
          const i = prev.findIndex(e => e.id === id);
          if (i === -1) {
            return [...prev, {id, node}];
          }
          const next = prev.slice();
          next[i] = {id, node};
          return next;
        }),
      remove: id => setEntries(prev => prev.filter(e => e.id !== id)),
    }),
    [],
  );

  return (
    <ModalHostContext.Provider value={api}>
      {children}
      {entries.map((entry, index) => (
        <View
          key={entry.id}
          pointerEvents="box-none"
          style={[StyleSheet.absoluteFill, {zIndex: BASE_Z + index}]}>
          {entry.node}
        </View>
      ))}
    </ModalHostContext.Provider>
  );
}

export function ModalPortal({children}: {children: ReactNode}) {
  const host = useModalHost();
  const id = useId();

  useEffect(() => {
    host.upsert(id, children);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  useEffect(() => {
    return () => host.remove(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}
